import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Tooltip,
    Alert,
    CircularProgress,
    Divider,
    Card,
    CardContent
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    PersonAdd as PersonAddIcon,
    Warning as WarningIcon,
    CheckCircle as CheckIcon,
    Schedule as PendingIcon,
    Refresh as RefreshIcon,
    SwapHoriz as ReassignIcon
} from '@mui/icons-material';
import axiosInstance from '../../../../../API/axios';
import SupportRequestDialog from './SupportRequestDialog/SupportRequestDialog';
import ReassignSupportRequestDialog from './ReassignSupportRequestDialog/ReassignSupportRequestDialog';
import SupportDetailDialog from './SupportDetailDialog/SupportDetailDialog';
import { DeletePopup } from '@Components/Popup/DeletePopup';
import { format } from 'date-fns';

const STATUS_COLORS = {
    PENDING: 'warning',
    RESPONDED: 'success',
    REASSIGNED: 'info',
    COMPLETED: 'default',
    CANCELLED: 'error'
};

const STATUS_ICONS = {
    PENDING: <PendingIcon fontSize="small" />,
    RESPONDED: <CheckIcon fontSize="small" />,
    REASSIGNED: <ReassignIcon fontSize="small" />
};

const SupportTab = ({ 
    exitPlanId, 
    isEditable = true, 
    disabled = false,
    planStatus = 'DRAFT'
}) => {
    const [supportRequests, setSupportRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [regions, setRegions] = useState([]);
    
    // Dialog states
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingRequest, setEditingRequest] = useState(null);
    const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
    const [reassigningRequest, setReassigningRequest] = useState(null);
    
    // Detail dialog state
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [viewingRequest, setViewingRequest] = useState(null);

    // Delete popup states
    const [deletePopupOpen, setDeletePopupOpen] = useState(false);
    const [requestToDelete, setRequestToDelete] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const canEdit = isEditable && !disabled && 
                   planStatus !== 'SUBMITTED' && 
                   planStatus !== 'APPROVED';

    const fetchSupportRequests = useCallback(async () => {
        if (!exitPlanId) return;
        
        try {
            setLoading(true);
            const response = await axiosInstance.get(`/support-requests/exit-plan/${exitPlanId}`);
            setSupportRequests(response.data || []);
            setError(null);
        } catch (err) {
            console.error('Error fetching support requests:', err);
            setError('Failed to load support requests');
        } finally {
            setLoading(false);
        }
    }, [exitPlanId]);

    const fetchRegions = useCallback(async () => {
        try {
            const response = await axiosInstance.get('/region/all');
            console.log('Regions response:', response);
            setRegions(response.data || []);
        } catch (err) {
            console.error('Error fetching regions:', err);
        }
    }, []);

    useEffect(() => {
        fetchSupportRequests();
        fetchRegions();
    }, [fetchSupportRequests, fetchRegions]);

    const handleOpenDetailDialog = (request) => {
        setViewingRequest(request);
        setDetailDialogOpen(true);
    };

    const handleCloseDetailDialog = () => {
        setDetailDialogOpen(false);
        setViewingRequest(null);
    };

    const handleOpenCreateDialog = () => {
        setEditingRequest(null);
        setDialogOpen(true);
    };

    const handleOpenEditDialog = (request) => {
        setEditingRequest(request);
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setEditingRequest(null);
    };

    const handleOpenReassignDialog = (request) => {
        setReassigningRequest(request);
        setReassignDialogOpen(true);
    };

    const handleCloseReassignDialog = () => {
        setReassignDialogOpen(false);
        setReassigningRequest(null);
    };

    const handleDeleteRequest = (requestId) => {
        setRequestToDelete(requestId);
        setDeletePopupOpen(true);
    };

    const handleCloseDeletePopup = () => {
        setDeletePopupOpen(false);
        setRequestToDelete(null);
    };

    const handleConfirmDelete = async (requestId) => {
        setDeleteLoading(true);
        try {
            await axiosInstance.delete(`/support-requests/${requestId}`);
            fetchSupportRequests();
            handleCloseDeletePopup();
        } catch (err) {
            console.error('Error deleting support request:', err);
            setError('Failed to delete support request');
        } finally {
            setDeleteLoading(false);
        }
    };

    const formatDate = (dateValue) => {
        if (!dateValue) return '-';
        try {
            let date;
            if (Array.isArray(dateValue)) {
                // Backend sends LocalDateTime as array: [year, month, day, hour, minute, second, nano]
                const [year, month, day, hour = 0, minute = 0, second = 0] = dateValue;
                date = new Date(year, month - 1, day, hour, minute, second);
            } else if (typeof dateValue === 'string') {
                date = new Date(dateValue);
            } else {
                return String(dateValue);
            }
            if (isNaN(date.getTime())) return String(dateValue);
            return format(date, 'MMM dd, yyyy HH:mm');
        } catch {
            return String(dateValue);
        }
    };

    if (loading && supportRequests.length === 0) {
        return (
            <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6" fontWeight="bold">
                    Support Requests
                </Typography>
                <Box display="flex" gap={1}>
                    <Tooltip title="Refresh">
                        <IconButton onClick={fetchSupportRequests} size="small">
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                    {canEdit && (
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleOpenCreateDialog}
                        >
                            Create Support Request
                        </Button>
                    )}
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <Alert severity="info" sx={{ mb: 2 }}>
                Create support requests to get assistance from Support Providers. If the email you enter 
                doesn't belong to an existing user, a new Support Provider account will be automatically created.
            </Alert>

            {supportRequests.length === 0 ? (
                <Card variant="outlined">
                    <CardContent>
                        <Box textAlign="center" py={4}>
                            <PersonAddIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                            <Typography variant="h6" color="textSecondary" gutterBottom>
                                No Support Requests
                            </Typography>
                            <Typography variant="body2" color="textSecondary" mb={2}>
                                Add support requests to get assistance from your organization's support providers.
                            </Typography>
                            {canEdit && (
                                <Button
                                    variant="outlined"
                                    startIcon={<AddIcon />}
                                    onClick={handleOpenCreateDialog}
                                >
                                    Create First Support Request
                                </Button>
                            )}
                        </Box>
                    </CardContent>
                </Card>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'grey.100' }}>
                                <TableCell><strong>#</strong></TableCell>
                                <TableCell><strong>Provider</strong></TableCell>
                                <TableCell><strong>Support Query</strong></TableCell>
                                <TableCell><strong>Response</strong></TableCell>
                                <TableCell><strong>Status</strong></TableCell>
                                <TableCell><strong>Created</strong></TableCell>
                                {canEdit && <TableCell><strong>Actions</strong></TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {supportRequests.map((request, index) => (
                                <TableRow 
                                    key={request.id}
                                    hover
                                    onClick={() => handleOpenDetailDialog(request)}
                                    sx={{
                                        borderLeft: request.userAutoCreated ? '3px solid orange' : 'none',
                                        cursor: 'pointer',
                                        '&:hover': { bgcolor: 'action.hover' }
                                    }}
                                >
                                    <TableCell>{index + 1}</TableCell>
                                    <TableCell>
                                        <Box>
                                            <Typography variant="body2" fontWeight="medium">
                                                {request.providerUsername}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                {request.providerEmail}
                                            </Typography>
                                            {request.userAutoCreated && (
                                                <Chip
                                                    label="New User"
                                                    size="small"
                                                    color="warning"
                                                    sx={{ ml: 1 }}
                                                />
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip title={request.supportQuery}>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    maxWidth: 200,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                {request.supportQuery}
                                            </Typography>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell>
                                        {request.supportResponse ? (
                                            <Tooltip title={request.supportResponse}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        maxWidth: 200,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                >
                                                    {request.supportResponse}
                                                </Typography>
                                            </Tooltip>
                                        ) : (
                                            <Typography variant="body2" color="textSecondary" fontStyle="italic">
                                                No response yet
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            icon={STATUS_ICONS[request.status]}
                                            label={request.status}
                                            color={STATUS_COLORS[request.status] || 'default'}
                                            size="small"
                                        />
                                        {request.isOverdue && (
                                            <Tooltip title="This request is overdue">
                                                <WarningIcon 
                                                    color="error" 
                                                    fontSize="small" 
                                                    sx={{ ml: 1 }} 
                                                />
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {formatDate(request.createdAt)}
                                        </Typography>
                                        {request.responseDate && (
                                            <Typography variant="caption" color="textSecondary" display="block">
                                                Responded: {formatDate(request.responseDate)}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    {canEdit && (
                                        <TableCell>
                                            <Box display="flex" gap={0.5} onClick={(e) => e.stopPropagation()}>
                                                <Tooltip title="Edit">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleOpenEditDialog(request)}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                {request.isOverdue && (
                                                    <Tooltip title="Reassign">
                                                        <IconButton
                                                            size="small"
                                                            color="warning"
                                                            onClick={() => handleOpenReassignDialog(request)}
                                                        >
                                                            <ReassignIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                <Tooltip title="Delete">
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleDeleteRequest(request.id)}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Detail View Dialog */}
            <SupportDetailDialog
                open={detailDialogOpen}
                onClose={handleCloseDetailDialog}
                request={viewingRequest}
            />

            {/* Create/Edit Dialog */}
            <SupportRequestDialog
                open={dialogOpen}
                onClose={handleCloseDialog}
                exitPlanId={exitPlanId}
                supportRequest={editingRequest}
                onSaved={fetchSupportRequests}
                regions={regions}
            />

            {/* Reassign Dialog */}
            <ReassignSupportRequestDialog
                open={reassignDialogOpen}
                onClose={handleCloseReassignDialog}
                supportRequest={reassigningRequest}
                onSaved={fetchSupportRequests}
                regions={regions}
            />

            {/* Delete Confirmation Popup */}
            {DeletePopup(
                'Support Request',
                deletePopupOpen,
                handleCloseDeletePopup,
                handleConfirmDelete,
                requestToDelete,
                deleteLoading,
                'Delete'
            )}
        </Box>
    );
};

export default SupportTab;
